import _ from 'lodash';
import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';
import Loading from '../../../components/loading/loading';
import FlashcardRunMode from '../../../model/enums/flashcard-run-mode';
import { FlashcardRun, FlashcardRunMetric } from '../../../model/flashcard/flashcard-run';
import FlashcardRunService from '../../../services/flashcard-run.service';
import { convertToTitleCase } from '../../../shared/util/utils';
import FlashcardHeader from '../components/flashcard-header';
import FlashcardModalReset from '../components/flashcard-modal-reset/flashcard-modal-reset';
import FlashcardRunCard from '../components/flashcard-run-card/flashcard-run-card';
import { SectionTitleContainer } from '../components/flashcard-run-card/flashcard-run-card.styles';
import { Container, MainContainer } from '../flashcard-styles';
import '../flashcard.scss';
import { ContentContainer, DotsIcon, Subtitle, Title } from '../flashcard-extensive-dashboard/flashcard-extensive-dashboard.styles';

interface Props extends RouteComponentProps<{}, {}, { id?: number }>, WithTranslation {
    hasSubscription?: boolean;
    openPaidModal: () => void;
}

type Section = {
    title: number;
    startDate: Moment;
    endDate: Moment;
    isFinished: boolean;
    data: FlashcardRunMetric[];
};

const FlashcardIntensiveDashboard = (props: Props) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [sections, setSections] = useState<Section[]>([]);
    const [showResetModal, setShowResetModal] = useState<boolean>(false);
    const [intensive, setIntensive] = useState<FlashcardRun>();

    useEffect(() => {
        FlashcardRunService.getFlashcardRunByMode(FlashcardRunMode.INTENSIVE).then(it => {
            if (it != null) {
                setIntensiveInformation(it);
            } else {
                props.history.push('/flashcard-intensive-create');
            }
        }).finally(() => setIsLoading(false));
    }, []);

    const setIntensiveInformation = (newIntensive: FlashcardRun) => {
        setIsLoading(true);
        setIntensive(newIntensive);
        const newSections: Section[] = [];
        const metric: FlashcardRunMetric[] = newIntensive!.metric! as FlashcardRunMetric[];
        let startDate = moment(newIntensive!.startDate);
        let weekNumber = 1;
        let currentWeek = -1;
        let currentWeekDay = 1;
        while (moment(startDate).isBefore(moment(newIntensive!.endDate!))) {
            const checkIfItIsThisWeek = (currentDate: Moment, startWeekDate: Moment): boolean =>
                currentDate.isBetween(startWeekDate.clone().subtract(1, 'days'), startWeekDate.clone().endOf('week').add(1, 'days'));
            const data: FlashcardRunMetric[] = _.reverse(metric.filter(it => checkIfItIsThisWeek(moment(it.date), startDate)));
            if (checkIfItIsThisWeek(moment(), startDate)) {
                currentWeek = weekNumber;
                currentWeekDay = data.findIndex(it => moment().isSame(moment(it.date), 'days')) + 1;
            }
            const newSection: Section = {
                title: weekNumber,
                data,
                isFinished: false,
                startDate: startDate!,
                endDate: moment(Math.max(...data.map(it => moment(it.date).toDate() as unknown as number))),
            };
            weekNumber++;
            startDate = startDate.clone().endOf('week').add(2, 'days');
            newSections.push(newSection);
        }
        setSections(newSections);
        setTimeout(() => {
            const index = currentWeek * currentWeekDay;
            const element = document.getElementsByClassName(`flashcard-run-card-container`)[index === 0 ? metric.length - 1 : metric.length - index];
            try{
                element.scrollIntoView({
                    behavior: 'smooth'
                });
            } catch (e) {}
            setIsLoading(false);
        }, 800);
    };

    const resetIntensive = () => {
        setShowResetModal(false);
        setIsLoading(true);
        FlashcardRunService.resetFlashcardRunByMode(FlashcardRunMode.INTENSIVE).then(() => {
            props.history.push('/flashcard-dashboard');
        });
    };

    const renderCard = (item: FlashcardRunMetric, key: string) => {
        const cards = sections.map(it => it.data).flat(1);
        const allBeforeIsFinished = cards.filter(it => it.id < item.id).every(it => it.isFinished);
        const isDisabled = !moment(item.date).isSameOrBefore(moment()) && !allBeforeIsFinished;
        return (
            <FlashcardRunCard
                key={key}
                metric={item}
                totalCards={intensive?.questionPerDay}
                title={convertToTitleCase(moment(item.date).format('dddd'))}
                isDisabled={isDisabled}
                onClick={() => props.history.push(`/flashcard-intensive/${item.id}`)}
                isCheckpoint={false}
                subtitle={moment(item.date).format('DD/MM/YYYY')} />);
    };

    const orderedSections = sections.sort((a, b) => b.title - a.title);
    return isLoading ? (
        <div className="disease-screen-container">
            <Loading />
        </div>
    ) : (
        <MainContainer>
            <Container>
                <FlashcardHeader
                    {...props}
                    title={props.t('flashcardIntensive.subtitle')}
                    subtitle={props.t('flashcardIntensive.title')}
                    viewMode={'CARD'}
                    onPressBack={() => props.history.push('/flashcard-dashboard')}>
                    <DotsIcon onClick={() => setShowResetModal(true)} className={'dots-three-vertical-img'} />
                </FlashcardHeader>
                <ContentContainer>
                    {orderedSections.map(section => <div key={section.title}>
                        <SectionTitleContainer>
                            <Title>{`Semana ${section.title}`}</Title>
                            <Subtitle>{` • ${moment(section.startDate).clone().format('DD MMM')} a ${moment(section.endDate).clone().format('DD MMM')}`}</Subtitle>
                        </SectionTitleContainer>
                        {section.data.map((item, i) => renderCard(item, `${section.title}-${i}`))}
                    </div>)}
                </ContentContainer>
                <FlashcardModalReset
                    show={showResetModal}
                    onPressConfirm={resetIntensive}
                    onClose={() => setShowResetModal(false)}
                    title={props.t('flashcardIntensive.resetModalTitle')}
                    subtitle={props.t('flashcardIntensive.resetModalSubtitle')}
                    confirmLabel={props.t('flashcardIntensive.resetModalConfirm')}
                />
            </Container>
        </MainContainer>
    );
};

const mapDispatchToProps = {};

export default compose(connect(null, mapDispatchToProps), withTranslation())(FlashcardIntensiveDashboard) as React.ComponentType<any>;
